import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ChangePassword() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const forced = user?.force_password_change;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refresh();
      toast.success("Password updated");
      navigate(["superadmin", "editor", "analyst", "social_manager"].includes(user?.role || "") ? "/admin" : "/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-[#121212] border border-white/10 rounded-xl p-8" data-testid="change-password-form">
        <div className="flex justify-center mb-6"><Logo /></div>
        <h1 className="font-heading font-bold text-white text-xl text-center mb-1">
          {forced ? "Set your new password" : "Change password"}
        </h1>
        {forced && (
          <p className="text-yellow-400/90 text-sm text-center mb-8 bg-yellow-500/10 border border-yellow-500/20 rounded p-3">
            For security, you must replace your temporary password before continuing.
          </p>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-gray-300">Current password</Label>
            <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
              data-testid="current-password-input" className="mt-1.5 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-gray-300">New password (min 8 characters)</Label>
            <Input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)}
              data-testid="new-password-input" className="mt-1.5 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="change-password-error">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="change-password-submit"
            className="w-full bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
            {busy ? "Updating..." : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
